import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import { memo, useCallback, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import Foreign from 'shell/components/shared/consult-person/steps/registry-office/generality/foreign';
import Italian from 'shell/components/shared/consult-person/steps/registry-office/generality/italian';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import { regexNumericFiscalCode } from 'shell/constants/regex';

interface FormGeneralityProps {
  isEditMode?: boolean;
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  person?: AccordionPersonForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  errorsPerson?: FormikErrors<AccordionPersonForm>;
  touchedPerson?: FormikTouched<AccordionPersonForm>;
  onChangePerson?: (
    key: keyof AccordionPersonForm,
    value: AccordionPersonForm[keyof AccordionPersonForm]
  ) => void;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
  onChangeFieldsPerson?: (values: AccordionPersonForm) => void;
  disabledBirthDate?: boolean;
  labelMunicipalityItalian?: string;
  labelProvinceItalian?: string;
  ForPracticeTemporary?: boolean;
  requiredFiscalCode?: boolean;
  disabledValidityFiscalCode?: boolean;
}

const FormGeneralityComponent = ({
  isEditMode,
  disabled,
  generality,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangeFieldsGenerality,
  disabledBirthDate = false,
  labelMunicipalityItalian,
  labelProvinceItalian,
  requiredFiscalCode = false,
}: FormGeneralityProps): React.ReactElement => {
  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreign', {
        ...(generality || {}).foreign,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('gender', event.target.value);
  });

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeFieldsGenerality({
        birthDate: eventDate,
        birthChoiceDate: choice,
      });
    }
  );

  const formContext = useFormikContext<NewPersonVM>();

  const fiscalCodeMaxLength = useMemo(() => {
    if (generality?.fiscalCode) {
      if (
        regexNumericFiscalCode.test(generality?.fiscalCode) &&
        generality?.fiscalCode?.length >= 10
      ) {
        return '11';
      }
      return '16';
    }
  }, [generality?.fiscalCode]);

  return (
    <Grid container spacing={2}>
      <Grid item container spacing={2} xs={12} md={10}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label={requiredFiscalCode ? 'Codice fiscale *' : 'Codice fiscale'}
            aria-label="Codice fiscale"
            value={generality?.fiscalCode?.toUpperCase() || ''}
            onChange={(event) => {
              onChangeGenerality('fiscalCode', event.target.value);
            }}
            helperText={errorsGenerality?.fiscalCode}
            error={touchedGenerality?.fiscalCode && Boolean(errorsGenerality?.fiscalCode)}
            fullWidth
            name="generality.fiscalCode"
            onBlur={formContext?.handleBlur}
            disabled={isEditMode ? false : disabled}
            inputProps={{ maxLength: fiscalCodeMaxLength ? fiscalCodeMaxLength : '16' }}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={generality?.withoutSurname ? 'Cognome' : 'Cognome *'}
            aria-label="Cognome"
            id="surname"
            name="generality.surname"
            onBlur={formContext?.handleBlur}
            fullWidth
            disabled={isEditMode && !generality?.withoutSurname ? false : disabled}
            helperText={errorsGenerality?.surname}
            error={touchedGenerality?.surname && Boolean(errorsGenerality?.surname)}
            value={generality?.surname || ''}
            inputProps={{
              maxLength: '80',
            }}
            onChange={(event) => {
              onChangeGenerality('surname', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname-replica"
                checked={generality?.withoutSurname || false}
                onChange={() => {
                  if (onChangeFieldsGenerality) {
                    onChangeFieldsGenerality({
                      withoutSurname: !generality?.withoutSurname,
                      surname: !generality?.withoutSurname ? '' : generality?.surname,
                    });
                  }
                }}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={generality?.withoutName ? 'Nome' : 'Nome *'}
            aria-label="Nome"
            id="name"
            name="generality.name"
            onBlur={formContext?.handleBlur}
            fullWidth
            disabled={isEditMode && !generality?.withoutName ? false : disabled}
            inputProps={{
              maxLength: '80',
            }}
            helperText={errorsGenerality?.name}
            error={touchedGenerality?.name && Boolean(errorsGenerality?.name)}
            value={generality?.name || ''}
            onChange={(event) => {
              onChangeGenerality('name', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName-replica"
                checked={generality?.withoutName || false}
                onChange={() => {
                  if (onChangeFieldsGenerality) {
                    onChangeFieldsGenerality({
                      withoutName: !generality?.withoutName,
                      name: !generality?.withoutName ? '' : generality?.name,
                    });
                  }
                }}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={4}>
          <ListGender
            required
            value={generality?.gender || ''}
            onChange={handleListGenderChange}
            disabled={isEditMode ? false : disabled}
            error={Boolean(errorsGenerality?.gender) && touchedGenerality?.gender}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          required
          selected={generality?.birthDate || null}
          onChange={handleChooseDateChange}
          choice={generality?.birthChoiceDate}
          disabled={isEditMode ? false : disabled || disabledBirthDate}
          error={touchedGenerality?.birthDate && Boolean(errorsGenerality?.birthDate)}
          helperText={errorsGenerality?.birthDate}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeign"
            value={generality?.isForeign || 'false'}
            onChange={() => {
              if (onChangeFieldsGenerality) {
                onChangeFieldsGenerality({
                  isForeign: !generality?.isForeign,
                  expatriationYear: '',
                  italian: {
                    location: '',
                    province: '',
                    municipality: null,
                  },
                  foreign: {
                    state: null,
                    province: '',
                    municipality: null,
                  },
                });
                return;
              }
              onChangeGenerality('isForeign', !generality?.isForeign);
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Nascita Italia"
              disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Nascita Estero"
              disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!generality?.isForeign ? (
        <Italian
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.italian}
          onChange={onFieldItalianChange}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
          touched={touchedGenerality?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality={labelMunicipalityItalian}
          labelProvince={labelProvinceItalian}
          birthday={generality?.birthDate}
        />
      ) : (
        <Foreign
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.foreign}
          onChange={onFieldForeignChange}
          errors={errorsGenerality?.foreign as FormikErrors<ForeignForm>}
          touched={touchedGenerality?.foreign as unknown as FormikTouched<ForeignForm>}
        />
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          aria-label="Luogo di nascita eccezionale"
          label="Luogo di nascita eccezionale"
          id="exceptionalBirthplace"
          name="exceptionalBirthplace"
          fullWidth
          disabled={isEditMode ? false : disabled}
          type="text"
          variant="outlined"
          inputProps={{
            maxLength: '120',
          }}
          helperText={errorsGenerality?.exceptionalBirthPlace}
          error={
            touchedGenerality?.exceptionalBirthPlace &&
            Boolean(errorsGenerality?.exceptionalBirthPlace)
          }
          value={generality?.exceptionalBirthPlace || ''}
          onChange={(event) => {
            onChangeGenerality('exceptionalBirthPlace', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Identificativo del Comune"
          label="Identificativo del Comune"
          id="municipalityId"
          fullWidth
          helperText={errorsGenerality?.municipalityIdentifier}
          error={
            touchedGenerality?.municipalityIdentifier &&
            Boolean(errorsGenerality?.municipalityIdentifier)
          }
          type="text"
          variant="outlined"
          value={generality?.municipalityIdentifier || ''}
          onChange={(event) => {
            onChangeGenerality('municipalityIdentifier', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Codice Istat"
          label="Codice Istat"
          id="istatCode"
          fullWidth
          type="text"
          variant="outlined"
          value={generality?.istatCode || ''}
          onChange={(event) => {
            onChangeGenerality('istatCode', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Identificativo ANPR"
          label="Identificativo ANPR"
          id="anprId"
          name="anprId"
          fullWidth
          type="text"
          variant="outlined"
          value={generality?.anprIdentifier || ''}
          onChange={(event) => {
            onChangeGenerality('anprIdentifier', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

FormGeneralityComponent.displayName = 'Form generality component';

const FormGenerality = memo(FormGeneralityComponent);

export default FormGenerality;
